import { Component, type ErrorInfo, type ReactNode } from 'react';

import { reportFailure } from '../observability/report';
import { ErrorState } from './states';

/**
 * The last line of defence.
 *
 * React unmounts the whole tree when a render throws, so without this a bug in
 * one cell renders as a white page -- the single worst failure mode, because it
 * looks identical to the application not existing. Whatever else is wrong, the
 * person should see a sentence explaining it and a way forward.
 *
 * The reset button reloads rather than trying to re-render: the session lives
 * in memory, so a reload also ends it, which is the right outcome when the
 * application's state is known to be broken.
 */
interface Props {
  children: ReactNode;
  /** Named in the failure report so a log line says which boundary caught it. */
  where: string;
}

interface State {
  error: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { error };
  }

  override componentDidCatch(error: unknown, _info: ErrorInfo): void {
    reportFailure(this.props.where, error);
  }

  override render(): ReactNode {
    if (this.state.error !== null) {
      return (
        <div className="page page--centred">
          <ErrorState
            error={this.state.error}
            title="A tela não pôde ser exibida"
            onRetry={() => {
              window.location.reload();
            }}
          />
        </div>
      );
    }
    return this.props.children;
  }
}
